import {
  Button,
  FlexBox,
  ModalBody,
  ModalHead,
  Text,
} from '@dixa/lib-shared-habla';
import { useTranslationDelete } from 'articleEditor/hooks/useTranslationDelete';
import { LanguageId } from 'lib-knowledge-management-common/graphql';
import { ModalWrapper, useModal } from 'lib-knowledge-modal';
import { useNotification } from 'lib-knowledge-utils/hooks/useNotification';
import { useCallback } from 'react';

type TranslationDeleteModalProps = {
  articleId: string;
  languageId: LanguageId;
  languageName: string;
  onAccept: () => void;
};

export function TranslationDeleteModal({
  articleId,
  languageName,
  languageId,
  onAccept,
}: TranslationDeleteModalProps): JSX.Element {
  const { hideModal } = useModal();
  const { successNotify, errorNotify } = useNotification();
  const [unPublishFn, { loading }] = useTranslationDelete({
    articleId,
    languageId,
  });

  const deleteTranslation = useCallback(() => {
    onAccept();
    unPublishFn()
      .then(() => {
        hideModal();
        successNotify({
          title: 'Success',
          text: `“${languageName}” translation has been deleted`,
        });
      })
      .catch(() => {
        errorNotify({
          title: 'Error',
          text: `“${languageName}” translation could not be deleted. Please try again or contact support.`,
        });
      });
  }, [
    unPublishFn,
    onAccept,
    hideModal,
    successNotify,
    languageName,
    errorNotify,
  ]);

  return (
    <ModalWrapper>
      <ModalHead
        title={`Delete ${languageName} translation`}
        onClose={hideModal}
      ></ModalHead>
      <ModalBody>
        <Text.Paragraph>
          Are you sure you want to delete the <strong>“{languageName}”</strong>{' '}
          translation for this article? Once deleted this can not be undone.
        </Text.Paragraph>
        <FlexBox justifyContent="flex-end" gap="100">
          <Button disabled={loading} variant="tertiary" onClick={hideModal}>
            Cancel
          </Button>
          <Button
            disabled={loading}
            onClick={deleteTranslation}
            variant="danger"
          >
            {loading ? 'Deleting...' : 'Delete'}
          </Button>
        </FlexBox>
      </ModalBody>
    </ModalWrapper>
  );
}
